import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axios from '../lib/axios';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [tenantName, setTenantName] = useState<string>('');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const navigate = useNavigate();

  // Get tenant info from subdomain or query param on load
  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        // First check if tenantId is in query params (from invite flow)
        const queryTenantId = searchParams.get('tenantId');
        if (queryTenantId && queryTenantId !== 'null' && queryTenantId !== 'undefined') {
          setTenantId(queryTenantId);
          // Fetch tenant name for this tenant ID
          const response = await axios.get('/platform/tenant/' + queryTenantId);
          if (response.data.data?.name) {
            setTenantName(response.data.data.name);
          }
          return;
        }

        const host = window.location.hostname;
        
        // Check if hostname is an IP address (4 numeric parts separated by dots)
        const isIpAddress = /^\d+\.\d+\.\d+\.\d+$/.test(host);
        
        // Only extract subdomain if it's NOT an IP address and NOT localhost
        if (!isIpAddress && host !== 'localhost') {
          // Extract subdomain (first part before .local or .com)
          const subdomain = host.split('.')[0];
          
          if (subdomain !== '127') {
            // This is a subdomain tenant - fetch tenant info
            const response = await axios.get('/platform/tenant-by-subdomain', {
              params: { subdomain }
            });
            if (response.data.data) {
              setTenantName(response.data.data.name);
              setTenantId(response.data.data.id);
            }
          }
        }
      } catch (err) {
        // Silent fail - tenant info not available
        console.debug('Tenant info fetch skipped');
      }
    };
    
    fetchTenantInfo();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError('Inserisci un indirizzo email valido');
      return;
    }

    const emailPattern = /^\S+@\S+\.\S+$/;
    if (!emailPattern.test(normalizedEmail)) {
      setError('Indirizzo email non valido');
      return;
    }

    try {
      await login(normalizedEmail, password, tenantId || undefined);
      // All users (including superadmin) go directly to dashboard
      // Tenant selection happens in the top bar for superadmin
      navigate('/dashboard/overview');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-mp-navy via-mp-sky to-mp-sky flex items-center justify-center p-4">
      <div className="w-full max-w-md p-8 bg-mp-white rounded-2xl shadow-2xl border border-mp-accent/10">
        <div className="mb-2 text-center">
          <h1 className="text-5xl font-black bg-gradient-to-r from-mp-accent to-orange-500 bg-clip-text text-transparent mb-2">SATI</h1>
          <p className="text-mp-gray-600 text-sm font-bold tracking-widest uppercase">Tourist Intelligence</p>
          {tenantName && (
            <p className="text-mp-accent text-sm font-semibold mt-3 flex items-center justify-center gap-2">

              {tenantName}
            </p>
          )}
        </div>
        <div className="h-1 w-16 bg-gradient-to-r from-mp-accent to-mp-alt mx-auto mb-8 rounded-full"></div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded text-sm font-medium">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-bold text-mp-gray-800 mb-2">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-mp-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mp-accent focus:border-transparent bg-mp-white text-mp-gray-800 placeholder-mp-gray-400 transition-all"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-mp-gray-800 mb-2">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-mp-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-mp-accent focus:border-transparent bg-mp-white text-mp-gray-800 placeholder-mp-gray-400 transition-all"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-mp-accent to-orange-600 text-mp-white font-bold py-3 rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Sign In
            </button>
          </form>
      </div>
    </div>
  );
}
